/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useState } from 'react';

import cn from 'classnames';
import { useRouter } from 'next/router';

import { Button } from 'ui/atoms/Button/Button';
import { Section } from 'ui/molecules/Section/Section';

import styles from './AIProductPricingSection.module.scss';

type Billing = 'monthly' | 'annual';

type MeterRow = { k: string; v: string };

type Plan = {
  tier: string;
  who: string;
  price: { monthly: string; annual: string };
  per?: string;
  billnote: { monthly: string; annual: string };
  cta: {
    label: string;
    href: string;
    hrefByBilling?: { monthly: string; annual: string };
    type: 'dark' | 'blue' | 'dark-outline';
  };
  featured?: boolean;
  meterLabel: string;
  meter: MeterRow[];
  featsHead: string;
  feats: string[];
};

export const AIProductPricingSection = () => {
  const { asPath } = useRouter();
  const [billing, setBilling] = useState<Billing>('monthly');

  const signupHref = `https://platform.globaldatabase.com/public/signup/regis?origin=${process.env.HOST}${asPath}`;
  const proHref = {
    monthly:
      'https://platform.globaldatabase.com/public/payment/summary?data%5B0%5D%5Btype%5D=product&data%5B0%5D%5Bdata%5D%5Bcountry_id%5D=&data%5B0%5D%5Bdata%5D%5Bproduct_id%5D=203&data%5B0%5D%5Bdata%5D%5Bfrequency%5D=month',
    annual:
      'https://platform.globaldatabase.com/public/payment/summary?data%5B0%5D%5Btype%5D=product&data%5B0%5D%5Bdata%5D%5Bcountry_id%5D=&data%5B0%5D%5Bdata%5D%5Bproduct_id%5D=203&data%5B0%5D%5Bdata%5D%5Bfrequency%5D=year',
  };

  const handleMonthly = useCallback(() => setBilling('monthly'), []);
  const handleAnnual = useCallback(() => setBilling('annual'), []);

  const plans: Plan[] = [
    {
      tier: 'Trial',
      who: 'No card required',
      price: { monthly: 'Free', annual: 'Free' },
      billnote: { monthly: 'No card required', annual: 'No card required' },
      cta: { label: 'Start free', href: signupHref, type: 'dark' },
      meterLabel: 'Plan',
      meter: [
        { k: 'Credits / mo', v: '5' },
        { k: 'CSV / PDF download', v: 'View only' },
        { k: 'API', v: '—' },
      ],
      featsHead: 'Includes',
      feats: [
        'Ask any company question in any language',
        'Interactive company profiles — data rendered visually, not just chat',
        'Verify by name, reg no., VAT or EIN',
        'View financials, directors, shareholders & group structure',
        'Full conversation history',
        'Source on every answer',
      ],
    },
    {
      tier: 'Pro',
      who: 'For professionals and small teams',
      price: { monthly: '$99', annual: '$83' },
      per: '/mo',
      billnote: { monthly: 'Billed monthly', annual: '$990 billed annually' },
      cta: { label: 'Subscribe', href: proHref.monthly, hrefByBilling: proHref, type: 'blue' },
      featured: true,
      meterLabel: 'Plan',
      meter: [
        { k: 'Credits / mo', v: '200' },
        { k: 'CSV / PDF download', v: 'View only' },
        { k: 'API', v: '—' },
      ],
      featsHead: 'Everything in Free, plus',
      feats: ['200 credits / month', 'Top up credits any time', 'Priority support'],
    },
    {
      tier: 'Corporate',
      who: 'Tailored to your data needs',
      price: { monthly: 'Custom', annual: 'Custom' },
      billnote: { monthly: 'Tailored to your data needs', annual: 'Tailored to your data needs' },
      cta: { label: 'Book a demo', href: '/book-a-demo', type: 'dark-outline' },
      meterLabel: 'What you can take',
      meter: [
        { k: 'Lookups', v: 'Unlimited' },
        { k: 'CSV / PDF download', v: 'Full + bulk' },
        { k: 'API', v: 'Included' },
      ],
      featsHead: 'Everything in Pro, plus',
      feats: [
        'Full API: KYB, ownership, directors, financials, linkage',
        'CSV & PDF download — single + bulk',
        'Quarterly bulk delivery',
        'Unlimited seats & lookups',
        'Redistribution & commercial licensing',
        'SSO, DPA, SLA',
        'Dedicated support',
      ],
    },
  ];

  return (
    <Section
      introBlockClassName={styles.intro}
      title="Simple, credit-based pricing"
      description="Every plan includes every data module — you're metered in credits, so you only pay for what you use."
      className={styles.section}
    >
      <div className={styles.toolbar}>
        <div className={styles.toggle} role="group" aria-label="Billing period">
          <button
            type="button"
            className={cn(styles.toggleBtn, billing === 'monthly' && styles.toggleBtnActive)}
            onClick={handleMonthly}
          >
            Monthly
          </button>
          <button
            type="button"
            className={cn(styles.toggleBtn, billing === 'annual' && styles.toggleBtnActive)}
            onClick={handleAnnual}
          >
            Annual <span className={styles.save}>2 months free</span>
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        {plans.map((plan) => (
          <div key={plan.tier} className={cn(styles.card, plan.featured && styles.cardFeatured)}>
            {plan.featured && <span className={styles.cardGlow} aria-hidden="true" />}
            {plan.featured && <span className={styles.ribbon}>Best for growth</span>}

            <div className={styles.tier}>{plan.tier}</div>
            <div className={styles.who}>{plan.who}</div>

            <div className={styles.price}>
              <span className={styles.amount}>{plan.price[billing]}</span>
              {plan.per && <span className={styles.per}>{plan.per}</span>}
            </div>
            <div className={styles.billnote}>{plan.billnote[billing]}</div>

            <Button
              type={plan.cta.type}
              size="lg"
              width="100%"
              href={plan.cta.hrefByBilling ? plan.cta.hrefByBilling[billing] : plan.cta.href}
              className={styles.cta}
            >
              {plan.cta.label}
            </Button>

            <div className={styles.meter}>
              <div className={styles.meterLabel}>{plan.meterLabel}</div>
              {plan.meter.map((row) => (
                <div key={row.k} className={styles.meterRow}>
                  <span className={styles.meterKey}>{row.k}</span>
                  <span className={styles.meterValue}>{row.v}</span>
                </div>
              ))}
            </div>

            <ul className={styles.feats}>
              <li className={styles.featsHead}>{plan.featsHead}</li>
              {plan.feats.map((feat) => (
                <li key={feat} className={styles.feat}>
                  <span className={styles.featIcon} aria-hidden="true">
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4.2 3.6 6.8 9 1"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  {feat}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
};
